import React, { useState, useEffect } from 'react';
import './ApproveBooking.css';
import { sendConfirmationEmail } from '../utils/emailService';
import {
  saveConfirmedBooking,
  ConfirmedBooking,
} from '../utils/bookingStorage';
import { getPublicSiteOrigin } from '../utils/siteUrl';

interface BookingApprovalData {
  bookingId: string;
  guestEmail: string;
  guestName: string;
  guestPhone: string;
  campName: string;
  campId: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  totalPrice: number;
}

const ApproveBooking: React.FC = () => {
  const [bookingData, setBookingData] = useState<BookingApprovalData | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = urlParams.get('data');
    if (dataParam) {
      try {
        const decoded = JSON.parse(decodeURIComponent(dataParam));
        setBookingData(decoded);
      } catch (error) {
        setApprovalStatus('error');
        setErrorMessage('Invalid booking data');
      }
    } else {
      setApprovalStatus('error');
      setErrorMessage('No booking data provided');
    }
  }, []);

  const handleApprove = async () => {
    if (!bookingData) return;

    setIsApproving(true);
    setApprovalStatus('idle');
    setErrorMessage('');

    try {
      const confirmedBooking: ConfirmedBooking = {
        id: bookingData.bookingId,
        campId: bookingData.campId,
        campName: bookingData.campName,
        checkIn: new Date(bookingData.checkIn),
        checkOut: new Date(bookingData.checkOut),
        guests: bookingData.guests,
        totalPrice: bookingData.totalPrice,
        guestName: bookingData.guestName,
        guestEmail: bookingData.guestEmail,
        guestPhone: bookingData.guestPhone || '',
        confirmedAt: new Date().toISOString(),
        status: 'confirmed',
      };

      const saved = await saveConfirmedBooking(confirmedBooking);
      if (!saved.success) {
        setApprovalStatus('error');
        setErrorMessage(
          saved.error
            ? `Could not save the booking to the database: ${saved.error}`
            : 'Could not save the booking to the database. Please try again.'
        );
        setIsApproving(false);
        return;
      }

      const origin = getPublicSiteOrigin();
      const paymentPageUrl = `${origin}/payment?bookingId=${encodeURIComponent(bookingData.bookingId)}`;
      const confirmationPdfPageUrl = `${origin}/confirmation?bookingId=${encodeURIComponent(
        bookingData.bookingId
      )}`;

      const result = await sendConfirmationEmail({
        bookingId: bookingData.bookingId,
        guestEmail: bookingData.guestEmail,
        guestName: bookingData.guestName,
        campName: bookingData.campName,
        checkIn: new Date(bookingData.checkIn),
        checkOut: new Date(bookingData.checkOut),
        guests: bookingData.guests,
        totalPrice: bookingData.totalPrice,
        paymentPageUrl,
        confirmationPdfPageUrl,
      });

      if (result.success) {
        setApprovalStatus('success');
      } else {
        setApprovalStatus('error');
        setErrorMessage(
          result.error ||
            'The booking was saved but the confirmation email could not be sent. Share the payment link manually or retry from Supabase/email.'
        );
      }
    } catch (error) {
      setApprovalStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsApproving(false);
    }
  };

  if (!bookingData && approvalStatus === 'idle') {
    return (
      <div className="approve-booking-page">
        <div className="approve-booking-container">
          <div className="loading-message">Loading booking data...</div>
        </div>
      </div>
    );
  }

  if (approvalStatus === 'error') {
    return (
      <div className="approve-booking-page">
        <div className="approve-booking-container">
          <div className="error-message">
            <h2>Error</h2>
            <p>{errorMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  if (approvalStatus === 'success') {
    return (
      <div className="approve-booking-page">
        <div className="approve-booking-container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Booking Approved!</h2>
            <p>A confirmation email has been sent to {bookingData?.guestEmail} with a secure Stripe payment link.</p>
            <p className="success-note">
              The guest will receive a confirmation email with all booking details.
              The booking dates have been automatically blocked in the system.
            </p>
            <div className="success-actions">
              <a 
                href="/manage-bookings" 
                className="manage-link"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/manage-bookings';
                }}
              >
                Manage All Bookings →
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const checkInDate = new Date(bookingData!.checkIn);
  const checkOutDate = new Date(bookingData!.checkOut);
  const nights = Math.ceil(
    (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className="approve-booking-page">
      <div className="approve-booking-container">
        <div className="approval-header">
          <h1>Approve Booking Request</h1>
          <p className="subtitle">Review the booking details below and approve to send confirmation email to the guest.</p>
        </div>

        <div className="booking-details-card">
          <h2>Booking Details</h2>
          
          <div className="detail-section">
            <div className="detail-row">
              <span className="detail-label">Booking ID:</span>
              <span className="detail-value">{bookingData!.bookingId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Camp:</span>
              <span className="detail-value">{bookingData!.campName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Check-in:</span>
              <span className="detail-value">{checkInDate.toLocaleDateString()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Check-out:</span>
              <span className="detail-value">{checkOutDate.toLocaleDateString()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Nights:</span>
              <span className="detail-value">{nights}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Guests:</span>
              <span className="detail-value">{bookingData!.guests}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Total Price:</span>
              <span className="detail-value">${bookingData!.totalPrice.toFixed(2)}</span>
            </div>
          </div>

          <div className="guest-section">
            <h3>Guest Information</h3>
            <div className="detail-row">
              <span className="detail-label">Name:</span>
              <span className="detail-value">{bookingData!.guestName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{bookingData!.guestEmail}</span>
            </div>
          </div>
        </div>

        <div className="approval-actions">
          <button
            onClick={handleApprove}
            disabled={isApproving}
            className="approve-button"
          >
            {isApproving ? 'Sending Confirmation...' : 'Approve & Send Confirmation Email'}
          </button>
        </div>

        {errorMessage && (
          <div className="error-banner">
            <p>{errorMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApproveBooking;
